import { Formik, Field } from 'formik'
import { ReactElement, useCallback } from 'react'
import { v4 } from 'uuid'
import { Flexbox } from '../components/flexbox/flexbox'
import { ACLRole, IUser } from '../types/types'
import { useRecoilValue } from 'recoil'
import { accessRolesState } from '../states/access-roles-state'

interface FormErrors {
  email?: string
  password?: string
  firstName?: string
  lastName?: string
}

const INITIAL_VALUES = {
  id: v4(),
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  accessRole: ACLRole.CLIENT,
}

interface Props {
  onSubmit?: (values) => void
  initialValues?: any
  excludedFields?: Array<keyof IUser>
  buttonTitle?: string
}

const parseInitialValues = (payload: IUser | null) => {
  if (payload) {
    return {
      ...payload,
      accessRole: payload?.accessRole?.codename,
    }
  }

  return INITIAL_VALUES
}

const UserForm = (props: Props): ReactElement => {
  const accessRoles = useRecoilValue(accessRolesState)
  const { onSubmit, initialValues, excludedFields, buttonTitle } = props
  const onSubmitted = useCallback(async (values, { setSubmitting }) => {
    const status = await onSubmit?.(values)
  }, [])

  return (
    <Formik
      initialValues={parseInitialValues(initialValues)}
      onSubmit={onSubmitted}
      validate={(values) => {
        const errors: FormErrors = {}
        if (!values.email) {
          errors.email = 'Required'
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
          errors.email = 'Invalid email address'
        }

        if (!values.firstName) {
          errors.firstName = 'Required'
        }
        if (!values.lastName) {
          errors.lastName = 'Required'
        }
        return errors
      }}>
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
        // @ts-ignore
        <form onSubmit={handleSubmit}>
          <Flexbox isCol>
            <input
              type="email"
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete={'off'}
              placeholder="Email"
              value={values.email}
              className="input input-bordered w-full max-w-xs mb-1"
            />
            <label className="label">
              <span className="label-text-alt text-error">
                {errors.email && touched.email && errors.email.toString()}
              </span>
            </label>
            <input
              type="text"
              name="firstName"
              autoComplete={'off'}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="First name"
              value={values.firstName}
              className="input input-bordered w-full max-w-xs mb-1"
            />
            <label className="label">
              <span className="label-text-alt text-error">
                {errors.firstName && touched.firstName && errors.firstName.toString()}
              </span>
            </label>
            <input
              type="text"
              name="lastName"
              autoComplete={'off'}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Last name"
              value={values.lastName}
              className="input input-bordered w-full max-w-xs mb-1"
            />
            <label className="label">
              <span className="label-text-alt text-error">
                {errors.lastName && touched.lastName && errors.lastName.toString()}
              </span>
            </label>
            {!excludedFields?.includes('password') && (
              <>
                <input
                  type="password"
                  name="password"
                  autoComplete={'off'}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Password"
                  value={values.password}
                  className="input input-bordered w-full max-w-xs"
                />

                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.password && touched.password && errors.password.toString()}
                  </span>
                </label>
              </>
            )}

            <Field
              className="select select-bordered w-full max-w-xs"
              component="select"
              id="accessRole"
              name="accessRole">
              <option>[Select role]</option>
              {accessRoles.map((role) => {
                return (
                  <option key={role.codename} value={role.codename}>
                    {role.name}
                  </option>
                )
              })}
            </Field>
            <div>
              <button type="submit" className="btn mt-4" disabled={isSubmitting}>
                {buttonTitle || 'Register'}
              </button>
            </div>
          </Flexbox>
        </form>
      )}
    </Formik>
  )
}

export default UserForm